/**
 * HandoffDialog (Workspace v2 Slice 3b, Block C) — the "Hand off to" pop-up shown
 * after a stage advance, from the drawer's "Hand off…" action, and from the
 * board card's owner avatar. Pre-filled with the case's current process owner
 * (assignee_user_id). Pick a member → PATCH assignee_user_id (the existing path,
 * which bells the new owner); Skip → leave the owner as-is (never strands).
 * Handing back to a PRIOR owner (Alice → Bob → back to Alice) is just picking
 * them again here — the same picker undoes a wrong hand-off, as long as they
 * still hold an active, non-observer membership (the members list this dialog
 * is given already carries that filter).
 *
 * Discoverability fix: the current owner is named up front (the pre-selected
 * dropdown value alone reads as "nothing has happened yet", not "this is who
 * has it now"), and the confirm button names the pick — "Hand off to <name>"
 * — once one is made, so confirming a wrong pick never happens blind.
 *
 * This is the "hand off to / assign to" control (whose desk the case is on) —
 * NEVER "Assign care manager" (that's the roster control, the two-assigns rule).
 */

import { useState, type ReactNode } from 'react';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';
import { TextAction } from '@/components/workspace/TextAction';
import type { WorkspaceMemberWire } from '@/lib/api';
import { SelectField } from '@/components/primitives/SelectField';

export interface HandoffDialogProps {
  members: WorkspaceMemberWire[];
  currentUserId: string | null;
  /** The current owner's display name, for the "Currently with" line — null when unassigned. */
  currentUserName: string | null;
  submitting: boolean;
  onHandoff: (userId: string | null) => void;
  onSkip: () => void;
}

export function HandoffDialog({ members, currentUserId, currentUserName, submitting, onHandoff, onSkip }: HandoffDialogProps): ReactNode {
  const [userId, setUserId] = useState<string>(currentUserId ?? '');
  const picked = userId ? (members.find((m) => m.user_id === userId) ?? null) : null;
  const confirmLabel = picked ? `Hand off to ${picked.name}` : 'Hand off';

  return (
    <Dialog title="Hand off to" size="sm" testId="handoff-dialog" onClose={onSkip}>
        <p className="text-xs text-ink-soft mb-1" data-testid="handoff-current-owner">
          Currently with {currentUserName ? <span className="font-semibold text-ink">{currentUserName}</span> : 'no one'}.
        </p>
        <p className="text-xs text-ink-soft mb-4">Who has this case next? (Skip to leave it where it is.)</p>
        <label className="block text-sm text-ink mb-4">
          Owner
          <SelectField size="field" value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="handoff-select"
            className="w-full mt-1">
            <option value="">Unassigned</option>
            {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
          </SelectField>
        </label>
        <div className="flex justify-end gap-2">
          <TextAction data-testid="handoff-skip" onClick={onSkip}>Skip</TextAction>
          <Button size="sm" disabled={submitting} data-testid="handoff-confirm" onClick={() => onHandoff(userId || null)}>
            {confirmLabel}
          </Button>
        </div>
    </Dialog>
  );
}
